import React, { useState, useEffect } from "react";
import { HashRouter, Route, Routes, useNavigate } from "react-router-dom";
import { AppStateProvider } from "./appstates";
import "./App.css";
import Productlisting from "./container/productlisting";
import Inventorylist from "./container/Inventorylist";
import ProductDetail from "./container/ProductDetail";
import InventoryDetail from "./container/inventoryDetail";
import Login from "./container/Login";
import Forgotpassword from "./container/Forgotpassword";
import UserMachines from "./container/user-Machines";
import CAMdetails from "./container/CAMdetails";
import CMAMachines from "./container/CMAMachines";
import ServiceRequest from "./container/ServiceRequest";
import Profilepersonal from "./container/Profilepersonal";
import ServiceNotesStatus from "./container/ServiceNotesStatus";
import OTPVerification from "./container/OtpVerification";
import NewPassword from "./container/NewPassword";
import UserRegister from "./container/UserRegister";
import ErrorPage from "./container/ErrorPage";
import Footer from "./container/Footer";
// Google Translate widget is loaded directly in public/index.html (outside React)

// Hide portal header/footer when the app is embedded in another site.
// Activates if EITHER condition is true:
//   1. URL contains ?embed=1 (explicit opt-in, e.g. /#/equipment?embed=1)
//   2. The page is rendered inside an iframe (auto-detected)
// The iframe check makes this work even if the embedding site forgets ?embed=1.
export const isEmbedMode = () => {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  const query = hash.split("?")[1] || "";
  if (new URLSearchParams(query).get("embed") === "1") return true;
  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin iframes throw on window.top access — being blocked
    // is itself proof we are inside a frame.
    return true;
  }
};


function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/inventorylist" element={<Inventorylist />} />
      <Route path="/equipment" element={<Productlisting />} />
      <Route path="/productDetails" element={<ProductDetail />} />
      <Route path="/inventoryDetails" element={<InventoryDetail />} />
      <Route path="/Login" element={<Login />} />
      <Route path="/User-Register" element={<UserRegister />} />
      <Route path="/user-Machines" element={<UserMachines />} />
      <Route path="/CAMdetails/:id" element={<CAMdetails />} />
      <Route path="/CMAMachines" element={<CMAMachines />} />
      <Route path="/ServiceRequest" element={<ServiceRequest />} />
      <Route path="/Profilepersonal" element={<Profilepersonal />} />
      <Route
        path="/ServiceNotesStatus/:id"
        element={<ServiceNotesStatus />}
      />
      <Route path="/Forgot-Password" element={<Forgotpassword />} />
      <Route path="/otp-verification" element={<OTPVerification />} />
      <Route path="/NewPassword" element={<NewPassword />} />
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
}

function App() {
  const embedded = isEmbedMode();
  return (
    <AppStateProvider>
      <HashRouter>
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <div style={{ flex: 1 }}>
            <AuthHandler>
              <AppRoutes />
            </AuthHandler>
          </div>
          {!embedded && <Footer />}
        </div>
      </HashRouter>
    </AppStateProvider>
  );
}

const PUBLIC_ROUTES = ["/equipment", "/inventorylist", "/productDetails", "/inventoryDetails"];

const LOGIN_ROUTES = ["/", "/login", "/user-register", "/forgot-password", "/otp-verification", "/newpassword"];

function AuthHandler({ children }) {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const currentHash = (window.location.hash.replace("#", "") || "/").split("?")[0];
    const currentLower = currentHash.toLowerCase();

    const isPublicRoute = PUBLIC_ROUTES.some(
      (route) => currentLower.startsWith(route.toLowerCase())
    );
    const isLoginRoute = LOGIN_ROUTES.some(
      (route) => currentLower === route.toLowerCase()
    );

    // Public routes - no auth needed
    if (isPublicRoute) {
      setLoading(false);
      return;
    }

    // Just logged out - skip auto-auth
    if (sessionStorage.getItem("justLoggedOut") === "1") {
      sessionStorage.removeItem("justLoggedOut");
      if (!isLoginRoute) {
        navigate("/Login", { replace: true });
      }
      setLoading(false);
      return;
    }

    // Already have token - user is logged in
    if (sessionStorage.getItem("token")) {
      if (isLoginRoute) {
        navigate("/user-Machines", { replace: true });
      }
      setLoading(false);
      return;
    }

    const waitForCatalystSDK = async (timeoutMs = 1500) => {
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        if (
          window.catalyst &&
          window.catalyst.auth &&
          typeof window.catalyst.auth.isUserAuthenticated === "function"
        ) {
          return true;
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      return false;
    };

    const withTimeout = (promise, ms, label) =>
      Promise.race([
        promise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(label + " timed out")), ms)
        ),
      ]);

    // Fire-and-forget token fetch. The dashboard does not need this to render —
    // it only reads `email` from sessionStorage. Awaiting it would add up to
    // 5 seconds to the visible loading time after sign-in.
    const fetchTokenInBackground = (username, firstName, lastName) => {
      const tokenController = new AbortController();
      const tokenTimer = setTimeout(() => tokenController.abort(), 8000);
      fetch(
        "https://appsail-10083322628.development.catalystappsail.com/api/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: username,
            first_name: firstName,
            last_name: lastName,
          }),
          signal: tokenController.signal,
        }
      )
        .then((r) => r.json())
        .then((tokenData) => {
          sessionStorage.setItem("token", tokenData.TOKEN || "authenticated");
        })
        .catch((tokenErr) => {
          console.error("Token fetch error", tokenErr);
          sessionStorage.setItem("token", "authenticated");
        })
        .finally(() => clearTimeout(tokenTimer));
    };

    const authenticateUser = async () => {
      try {
        const sdkReady = await waitForCatalystSDK(1500);
        if (!sdkReady) {
          throw new Error("Catalyst SDK not available");
        }

        const authResult = await withTimeout(
          window.catalyst.auth.isUserAuthenticated(),
          4000,
          "isUserAuthenticated"
        );

        const username = authResult.content.email_id;
        const firstName = authResult.content.first_name;
        const lastName = authResult.content.last_name;
        const userManagement = window.catalyst.userManagement;
        const currentUserResponse = await withTimeout(
          userManagement.getCurrentProjectUser(),
          4000,
          "getCurrentProjectUser"
        );
        const userRole = currentUserResponse.content.role_details.role_name;
        const roleId = currentUserResponse.content.role_details.role_id;
        const userId = currentUserResponse.content.user_id;
        const email = currentUserResponse.content.email_id;

        sessionStorage.setItem("userRole", userRole);
        sessionStorage.setItem("roleId", roleId);
        sessionStorage.setItem("userId", userId);
        sessionStorage.setItem("email", email);
        sessionStorage.setItem("first_name", firstName);

        // Kick off the slow backend token call but do NOT block navigation on
        // it. The dashboard only reads `email`, so we can hand the user off
        // to /user-Machines immediately and let the token land async.
        fetchTokenInBackground(username, firstName, lastName);

        if (isLoginRoute) {
          navigate("/user-Machines", { replace: true });
        }
        setLoading(false);
      } catch (error) {
        console.error("Authentication check:", error.message);
        if (!isLoginRoute) {
          navigate("/Login", { replace: true });
        }
        setLoading(false);
      }
    };

    authenticateUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0a3d62 0%, #017cc2 55%, #3aa8e0 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontFamily: "Open Sans, sans-serif",
        }}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            border: "4px solid rgba(255,255,255,0.25)",
            borderTop: "4px solid #f3cf05",
            borderRadius: "50%",
            animation: "jad-auth-spin 1s linear infinite",
            marginBottom: "18px",
          }}
        />
        <div style={{ fontSize: "16px", fontWeight: 600, letterSpacing: "0.04em" }}>
          Signing you in…
        </div>
        <div style={{ fontSize: "13px", marginTop: "6px", color: "rgba(255,255,255,0.78)" }}>
          This usually takes a few seconds.
        </div>
        <style>{`@keyframes jad-auth-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  return children;
}
export default App;
